/**
 * Lógica compartida de columnas-árbol Año › Mes › Semana para las pantallas
 * Avance y Venta/Indirectos. Construye la lista plana de columnas a renderizar:
 * cada mes aparece como una columna; si el mes está expandido, sus semanas ISO
 * aparecen como sub-columnas inmediatamente después (reemplazando al mes en la
 * fila de datos — el mes expandido se muestra como cabecera con colspan).
 *
 * La granularidad de DATOS es independiente del render: las celdas de mes leen
 * del dataset mensual; las de semana, del semanal. Este módulo solo decide qué
 * columnas existen y en qué orden, no de dónde sale el valor.
 */

/** Una columna concreta a renderizar en el cuerpo de la tabla. */
export interface ColumnaHoja {
  /** Llave única de la celda en los Map<string, ...> de cada nodo. */
  key: string;
  anio: number;
  mes: number;
  /** Semana ISO si es columna de semana; null si es columna de mes. */
  semanaIso: number | null;
}

/** Mes que puede expandirse a semanas (para los headers y el toggle). */
export interface MesCol {
  mkey: string; // `${anio}-${mes}`
  anio: number;
  mes: number;
  /** Semanas ISO disponibles para este mes (asc), de los datos semanales. */
  semanas: number[];
}

export interface ArbolColumnas {
  /** Columnas hoja en orden de render (mes o semana). */
  hojas: ColumnaHoja[];
  /** Meses (para construir el header de mes con su colspan y el toggle). */
  meses: MesCol[];
  /** Años únicos (asc) para el header superior. */
  anios: number[];
}

/** Llave de celda mensual: `${anio}-${mes}`. */
export function keyMes(anio: number, mes: number): string {
  return `${anio}-${mes}`;
}
/** Llave de celda semanal: `${anio}-${mes}-w${semana}`. */
export function keySemana(anio: number, mes: number, semana: number): string {
  return `${anio}-${mes}-w${semana}`;
}

/**
 * Construye el árbol de columnas.
 * @param mesesData pares (anio, mes) presentes en el dataset MENSUAL.
 * @param semanasPorMes mapa `${anio}-${mes}` → semanas ISO presentes en el dataset SEMANAL.
 * @param expandidos set de `${anio}-${mes}` que el usuario expandió a semanas.
 */
export function construirArbolColumnas(
  mesesData: Array<{ anio: number; mes: number }>,
  semanasPorMes: Map<string, number[]>,
  expandidos: Set<string>,
): ArbolColumnas {
  // Dedup + orden de meses.
  const mesSet = new Map<string, { anio: number; mes: number }>();
  for (const { anio, mes } of mesesData) {
    mesSet.set(keyMes(anio, mes), { anio, mes });
  }
  const mesesOrden = [...mesSet.values()].sort((a, b) =>
    a.anio !== b.anio ? a.anio - b.anio : a.mes - b.mes,
  );

  const meses: MesCol[] = [];
  const hojas: ColumnaHoja[] = [];

  for (const { anio, mes } of mesesOrden) {
    const mkey = keyMes(anio, mes);
    // Orden cronológico dentro del mes. En diciembre, los días finales pueden caer
    // en la semana ISO 1 del año siguiente: esas semanas bajas van AL FINAL, no al
    // inicio (evita "S1, S49, S50…"). En enero las semanas altas (52/53) del año
    // ISO anterior van al inicio. Heurística: si el mes mezcla semanas bajas (≤6) y
    // altas (≥47), las bajas se tratan como "después" sumándoles 53.
    const crudas = semanasPorMes.get(mkey) ?? [];
    const hayAltas = crudas.some((s) => s >= 47);
    const ordinal = (s: number) => (mes === 12 && hayAltas && s <= 6 ? s + 53 : s);
    const semanas = crudas.slice().sort((a, b) => ordinal(a) - ordinal(b));
    meses.push({ mkey, anio, mes, semanas });

    if (expandidos.has(mkey) && semanas.length > 0) {
      // Mes expandido: una columna hoja por semana (el mes se vuelve cabecera).
      for (const s of semanas) {
        hojas.push({ key: keySemana(anio, mes, s), anio, mes, semanaIso: s });
      }
    } else {
      // Mes colapsado: una sola columna hoja de mes.
      hojas.push({ key: mkey, anio, mes, semanaIso: null });
    }
  }

  const anios = [...new Set(mesesOrden.map((m) => m.anio))].sort((a, b) => a - b);
  return { hojas, meses, anios };
}

/** Etiqueta corta de una columna de semana ("S23"). */
export function etiquetaSemana(semanaIso: number): string {
  return `S${semanaIso}`;
}
